import { Component } from '@theme/component';
import { debounce, requestIdleCallback } from '@theme/utilities';

/**
 * Manages a trigger + popover relationship and exposes trigger bounds as CSS variables.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} popover
 * @property {HTMLElement} trigger
 *
 * @extends Component<Refs>
 */
export class AnchoredPopoverComponent extends Component {
  requiredRefs = ['popover', 'trigger'];
  interaction_delay = 200;

  /** @type {number | null} */
  #popoverTrigger = null;

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { popover, trigger } = this.refs;

    if (this.dataset.closeOnResize) {
      popover.addEventListener('beforetoggle', this.#handleBeforeToggleResize, { signal });
    }

    if (this.dataset.hoverTriggered) {
      trigger.addEventListener('pointerenter', this.#handleTriggerEnter, { signal });
      trigger.addEventListener('pointerleave', this.#handleTriggerLeave, { signal });
      popover.addEventListener('pointerenter', this.#handlePopoverEnter, { signal });
      popover.addEventListener('pointerleave', this.#handlePopoverLeave, { signal });
    }

    if (!CSS.supports('position-anchor: --trigger')) {
      popover.addEventListener('beforetoggle', this.#updatePosition, { signal });
      requestIdleCallback(this.#updatePosition);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#clearPopoverTrigger();
    window.removeEventListener('resize', this.#resizeListener);
    this.#controller?.abort();
    this.#controller = null;
  }

  #handleTriggerEnter = () => {
    const { trigger, popover } = this.refs;

    trigger.dataset.hoverActive = 'true';
    this.#clearPopoverTrigger();

    if (!popover.matches(':popover-open')) {
      this.#popoverTrigger = window.setTimeout(() => {
        if (trigger.matches('[data-hover-active]')) {
          popover.showPopover();
        }
      }, this.interaction_delay);
    }
  };

  #handleTriggerLeave = () => {
    const { trigger, popover } = this.refs;

    delete trigger.dataset.hoverActive;
    this.#clearPopoverTrigger();

    if (popover.matches(':popover-open')) {
      this.#popoverTrigger = window.setTimeout(() => {
        popover.hidePopover();
      }, this.interaction_delay);
    }
  };

  #handlePopoverEnter = () => {
    this.#clearPopoverTrigger();
  };

  #handlePopoverLeave = () => {
    const { popover } = this.refs;

    this.#clearPopoverTrigger();
    this.#popoverTrigger = window.setTimeout(() => {
      popover.hidePopover();
    }, this.interaction_delay);
  };

  /**
   * @param {Event} event
   */
  #handleBeforeToggleResize = (event) => {
    const toggleEvent = /** @type {ToggleEvent} */ (event);

    if (toggleEvent.newState === 'open') {
      window.addEventListener('resize', this.#resizeListener);
      return;
    }

    window.removeEventListener('resize', this.#resizeListener);
  };

  #updatePosition = () => {
    const { popover, trigger } = this.refs;
    if (!popover || !trigger) return;

    const rect = trigger.getBoundingClientRect();

    popover.style.setProperty('--anchor-top', `${rect.top}px`);
    popover.style.setProperty('--anchor-right', `${window.innerWidth - rect.right}px`);
    popover.style.setProperty('--anchor-bottom', `${window.innerHeight - rect.bottom}px`);
    popover.style.setProperty('--anchor-left', `${rect.left}px`);
    popover.style.setProperty('--anchor-height', `${rect.height}px`);
    popover.style.setProperty('--anchor-width', `${rect.width}px`);
  };

  #resizeListener = debounce(() => {
    const popover = this.refs.popover;

    if (popover?.matches(':popover-open')) {
      popover.hidePopover();
    }
  }, 100);

  #clearPopoverTrigger() {
    if (this.#popoverTrigger === null) return;

    window.clearTimeout(this.#popoverTrigger);
    this.#popoverTrigger = null;
  }
}

if (!customElements.get('anchored-popover-component')) {
  customElements.define('anchored-popover-component', AnchoredPopoverComponent);
}